import React from 'react';
import {
  Tr,
  Td,
  Text,
  Badge,
  Avatar,
  HStack,
  Box,
  Image,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { Checkbox } from 'src/components/atoms/Checkbox';
import { InboxItem } from 'src/api/inboxes';
import { useUpdateMessageMutation } from 'src/api/inboxes';
import { useAppContext } from 'src/contexts/AppContext';
import { dayjs } from 'src/libs/dayjs';

type InboxListItemProps = {
  item: InboxItem;
  onToggleSelect: (id: string) => void;
};

export const InboxListItem: React.FC<InboxListItemProps> = ({
  item,
  onToggleSelect,
}) => {
  const navigate = useNavigate();
  const { currentCustomer } = useAppContext();
  const { mutateAsync: updateMessage } = useUpdateMessageMutation(
    currentCustomer?.customerName ?? 'missing_customer',
    item.id,
  );

  const handleRowClick = async () => {
    try {
      if (item.isUnread) {
        await updateMessage({ isRead: true });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating message:', error);
    }
    navigate(`/inbox/${item.id}`);
  };

  return (
    <Tr
      cursor="pointer"
      onClick={handleRowClick}
      borderBottom="1px solid"
      borderColor="surface.border">
      <Td px={6} py={4} w="60px">
        <HStack gap={3}>
          <Box
            onClick={(e) => e.stopPropagation()}
            display="flex"
            alignItems="center"
            justifyContent="center">
            <Checkbox
              isChecked={item.isSelected || false}
              onChange={() => onToggleSelect(item.id)}
              w="24px"
              h="24px"
            />
          </Box>
          {item.isUnread && (
            <Box w="8px" h="8px" borderRadius="full" bg="icon.green" />
          )}
        </HStack>
      </Td>

      <Td pr={4} py={4} w="250px">
        <HStack gap={2}>
          {item.originator.type === 'user' ? (
            <Avatar
              w="28px"
              h="28px"
              name={item.originator.name}
              src={item.originator.avatar}
            />
          ) : (
            <Box
              borderRadius="full"
              w="28px"
              h="28px"
              border="1px"
              borderColor="surface.border"
              display="flex"
              alignItems="center"
              justifyContent="center">
              <Image src="/favicon.svg" alt="Yapamar" boxSize={4} />
            </Box>
          )}
          <Text
            fontFamily="marketingPage"
            fontWeight="medium"
            color="content.title">
            {item.originator.name}
          </Text>
        </HStack>
      </Td>

      <Td px={4} py={4}>
        <HStack gap={2} alignItems="center" w="100%" overflow="hidden">
          {item.messageType === 'system' && (
            <Badge
              bg="background.green.200"
              color="icon.green"
              px={3}
              py={1}
              borderRadius="full"
              fontWeight="bold"
              fontSize="sm"
              textTransform="none"
              flexShrink={0}>
              System
            </Badge>
          )}
          <Text
            fontFamily="marketingPage"
            color="content.title"
            minW={0}
            noOfLines={1}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap">
            <Text as="span" fontWeight={item.isUnread ? 'bold' : 'normal'}>
              {item.title}
            </Text>
            <Text as="span" opacity={0.6} ml={2}>
              {item.description}
            </Text>
          </Text>
        </HStack>
      </Td>

      <Td px={6} py={4} w="180px" textAlign="right">
        <Text fontFamily="marketingPage" color="content.title">
          {dayjs(item.date).format('MMM D, YYYY')}
        </Text>
      </Td>
    </Tr>
  );
};
